/**
 * Field value extraction + sensitive-input detection for the assistant page
 * outline. Split out from `assistant-page-snapshot-describe.ts` so the
 * describer can stay under the 200-line cap.
 */
import { OUTFITTER_ASSISTANT_REDACT_ATTR } from "~components/outfitter-assistant/outfitter-assistant-constants"
import { CHECKABLE_ROLES } from "~utils/helpers/assistant-page-snapshot-states"

export const REDACTED_VALUE = "***"

const SENSITIVE_AUTOCOMPLETE_TOKENS = [
    "cc-number",
    "cc-csc",
    "cc-exp",
    "new-password",
    "current-password",
] as const

export function fieldValue(
    node: Element,
    role: string,
    truncate: (value: string) => string
): string | null {
    if (shouldRedact(node)) return REDACTED_VALUE
    // Checkable fields expose their state via [checked=...] instead of a
    // trailing `: value` segment, so we intentionally skip value output here.
    if (CHECKABLE_ROLES.has(role)) return null
    if (node instanceof HTMLSelectElement) return selectValue(node, truncate)
    return inputOrTextareaValue(node, truncate)
}

export function isPasswordLikeField(node: Element): boolean {
    if (!(node instanceof HTMLInputElement)) return false
    const type = (node.getAttribute("type") ?? "").toLowerCase()
    if (type === "password") return true
    const autocomplete = (node.getAttribute("autocomplete") ?? "").toLowerCase()
    return SENSITIVE_AUTOCOMPLETE_TOKENS.some((token) =>
        autocomplete.includes(token)
    )
}

function shouldRedact(node: Element): boolean {
    if (node.hasAttribute(OUTFITTER_ASSISTANT_REDACT_ATTR)) return true
    return isPasswordLikeField(node)
}

function selectValue(
    node: HTMLSelectElement,
    truncate: (value: string) => string
): string | null {
    const option = node.options[node.selectedIndex]
    if (!option) return null
    const label = truncate((option.textContent ?? "").trim())
    return `"${label}"`
}

function inputOrTextareaValue(
    node: Element,
    truncate: (value: string) => string
): string | null {
    const raw =
        node instanceof HTMLInputElement || node instanceof HTMLTextAreaElement
            ? node.value
            : node.getAttribute("value") ?? ""
    return raw ? `"${truncate(raw)}"` : null
}
